// File: src/components/LandingPage/LandingPage.jsx
// Modern landing page with storytelling illustrations - Combines 3 design concepts

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, CheckCircle, Clock, Users, ArrowRight, Sparkles, Brain,
  FileText, BarChart3, Upload, Star, Shield, Award, Play,
  Linkedin, Twitter, Github, Heart, Coffee,
  Target, Rocket, Crown, Building2, ThumbsUp, Eye, Download, Filter
} from 'lucide-react';

// SVG Illustration Components
const HeroIllustration = () => (
  <svg viewBox="0 0 500 400" className="w-full h-full">
    {/* Background Elements */}
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.1"/>
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1"/>
      </linearGradient>
      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#3B82F6"/>
        <stop offset="100%" stopColor="#8B5CF6"/>
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15"/>
      </filter>
    </defs>

    {/* Floating circles background */}
    <circle cx="50" cy="50" r="30" fill="url(#grad1)" className="animate-pulse"/>
    <circle cx="450" cy="100" r="20" fill="url(#grad1)" className="animate-pulse" style={{animationDelay: '0.5s'}}/>
    <circle cx="400" cy="350" r="40" fill="url(#grad1)" className="animate-pulse" style={{animationDelay: '1s'}}/>

    {/* Main Dashboard Card */}
    <g filter="url(#shadow)">
      <rect x="100" y="50" width="300" height="200" rx="16" fill="white" stroke="#E5E7EB" strokeWidth="1"/>

      {/* Dashboard Header */}
      <rect x="100" y="50" width="300" height="45" rx="16" fill="#F9FAFB"/>
      <rect x="115" y="65" width="80" height="6" rx="3" fill="#E5E7EB"/>
      <circle cx="370" cy="72" r="8" fill="#10B981"/>
      <circle cx="350" cy="72" r="8" fill="#F59E0B"/>
      <circle cx="330" cy="72" r="8" fill="#EF4444"/>

      {/* Stats Row */}
      <rect x="115" y="110" width="60" height="40" rx="8" fill="#EEF2FF"/>
      <text x="145" y="135" textAnchor="middle" fill="#4F46E5" fontSize="14" fontWeight="bold">98%</text>
      <text x="145" y="145" textAnchor="middle" fill="#6B7280" fontSize="8">Accuracy</text>

      <rect x="185" y="110" width="60" height="40" rx="8" fill="#ECFDF5"/>
      <text x="215" y="135" textAnchor="middle" fill="#059669" fontSize="14" fontWeight="bold">2min</text>
      <text x="215" y="145" textAnchor="middle" fill="#6B7280" fontSize="8">Per Job</text>

      <rect x="255" y="110" width="60" height="40" rx="8" fill="#FEF3C7"/>
      <text x="285" y="135" textAnchor="middle" fill="#D97706" fontSize="14" fontWeight="bold">500+</text>
      <text x="285" y="145" textAnchor="middle" fill="#6B7280" fontSize="8">Companies</text>

      <rect x="325" y="110" width="60" height="40" rx="8" fill="#FCE7F3"/>
      <text x="355" y="135" textAnchor="middle" fill="#DB2777" fontSize="14" fontWeight="bold">20x</text>
      <text x="355" y="145" textAnchor="middle" fill="#6B7280" fontSize="8">Faster</text>

      {/* Candidate List */}
      <rect x="115" y="165" width="270" height="30" rx="6" fill="#F0FDF4" stroke="#86EFAC" strokeWidth="1"/>
      <circle cx="135" cy="180" r="10" fill="url(#grad2)"/>
      <text x="135" y="183" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">1</text>
      <rect x="155" y="175" width="100" height="4" rx="2" fill="#22C55E"/>
      <text x="365" y="183" textAnchor="end" fill="#22C55E" fontSize="11" fontWeight="bold">95%</text>

      <rect x="115" y="200" width="270" height="30" rx="6" fill="#F0FDF4" stroke="#86EFAC" strokeWidth="1"/>
      <circle cx="135" cy="215" r="10" fill="url(#grad2)"/>
      <text x="135" y="218" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">2</text>
      <rect x="155" y="210" width="90" height="4" rx="2" fill="#22C55E"/>
      <text x="365" y="218" textAnchor="end" fill="#22C55E" fontSize="11" fontWeight="bold">89%</text>
    </g>

    {/* Floating Resume Cards */}
    <g filter="url(#shadow)" className="animate-bounce" style={{animationDuration: '3s'}}>
      <rect x="30" y="150" width="80" height="100" rx="8" fill="white" stroke="#E5E7EB"/>
      <rect x="40" y="165" width="40" height="4" rx="2" fill="#E5E7EB"/>
      <rect x="40" y="175" width="60" height="3" rx="1.5" fill="#F3F4F6"/>
      <rect x="40" y="183" width="55" height="3" rx="1.5" fill="#F3F4F6"/>
      <rect x="40" y="191" width="50" height="3" rx="1.5" fill="#F3F4F6"/>
      <rect x="40" y="210" width="30" height="20" rx="4" fill="#DBEAFE"/>
      <text x="55" y="223" textAnchor="middle" fill="#3B82F6" fontSize="8">PDF</text>
    </g>

    {/* AI Brain Icon */}
    <g filter="url(#shadow)">
      <circle cx="420" cy="200" r="35" fill="url(#grad2)"/>
      <text x="420" y="207" textAnchor="middle" fill="white" fontSize="24">🧠</text>
    </g>

    {/* Connection Lines */}
    <path d="M110 200 Q 80 200 80 180" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-pulse"/>
    <path d="M400 200 Q 420 180 420 165" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-pulse"/>

    {/* Sparkles */}
    <text x="60" y="280" fontSize="16" className="animate-ping">✨</text>
    <text x="440" cy="280" fontSize="16" className="animate-ping" style={{animationDelay: '0.3s'}}>✨</text>
    <text x="250" cy="320" fontSize="20" className="animate-ping" style={{animationDelay: '0.6s'}}>⭐</text>
  </svg>
);

const ProcessIllustration = ({ step }) => {
  const illustrations = {
    1: (
      <svg viewBox="0 0 120 120" className="w-full h-full">
        <defs>
          <linearGradient id="uploadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6"/>
            <stop offset="100%" stopColor="#8B5CF6"/>
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r="50" fill="#EEF2FF"/>
        <rect x="35" y="35" width="50" height="60" rx="4" fill="white" stroke="#CBD5E1" strokeWidth="2"/>
        <path d="M60 50 L60 70 M50 60 L60 50 L70 60" stroke="url(#uploadGrad)" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <rect x="42" y="80" width="36" height="4" rx="2" fill="#E2E8F0"/>
      </svg>
    ),
    2: (
      <svg viewBox="0 0 120 120" className="w-full h-full">
        <circle cx="60" cy="60" r="50" fill="#F0FDF4"/>
        <circle cx="60" cy="60" r="30" fill="white" stroke="#22C55E" strokeWidth="3"/>
        <text x="60" y="55" textAnchor="middle" fontSize="24">🤖</text>
        <text x="60" y="75" textAnchor="middle" fill="#22C55E" fontSize="10" fontWeight="bold">AI</text>
        {/* Scanning lines */}
        <line x1="20" y1="40" x2="40" y2="40" stroke="#22C55E" strokeWidth="2" className="animate-pulse"/>
        <line x1="80" y1="40" x2="100" y2="40" stroke="#22C55E" strokeWidth="2" className="animate-pulse"/>
        <line x1="20" y1="80" x2="40" y2="80" stroke="#22C55E" strokeWidth="2" className="animate-pulse"/>
        <line x1="80" y1="80" x2="100" y2="80" stroke="#22C55E" strokeWidth="2" className="animate-pulse"/>
      </svg>
    ),
    3: (
      <svg viewBox="0 0 120 120" className="w-full h-full">
        <circle cx="60" cy="60" r="50" fill="#FEF3C7"/>
        <text x="60" y="50" textAnchor="middle" fontSize="30">🏆</text>
        <rect x="30" y="70" width="60" height="8" rx="4" fill="#F59E0B"/>
        <rect x="35" y="82" width="50" height="6" rx="3" fill="#FCD34D"/>
        <rect x="40" y="92" width="40" height="5" rx="2.5" fill="#FDE68A"/>
      </svg>
    )
  };
  return illustrations[step];
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);

  const features = [
    { icon: <Brain className="w-8 h-8" />, title: "AI-Powered Analysis", desc: "Google Gemini analyzes resumes with human-like understanding" },
    { icon: <Clock className="w-8 h-8" />, title: "Save 20+ Hours", desc: "What takes days now takes minutes" },
    { icon: <BarChart3 className="w-8 h-8" />, title: "Detailed Insights", desc: "Match scores, skills analysis, strengths & weaknesses" },
    { icon: <CheckCircle className="w-8 h-8" />, title: "Accurate Ranking", desc: "Find the best candidates every time" }
  ];

  const steps = [
    { num: 1, title: "Upload", desc: "Drop your job description and resumes", icon: <Upload className="w-6 h-6" /> },
    { num: 2, title: "AI Analyzes", desc: "Our AI reads and understands every detail", icon: <Brain className="w-6 h-6" /> },
    { num: 3, title: "Get Results", desc: "Ranked candidates with detailed insights", icon: <Award className="w-6 h-6" /> }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "HR Director",
      company: "TechCorp Inc.",
      image: "👩‍💼",
      quote: "Reduced our hiring time by 80%. We found our senior developer in just 2 days instead of 3 weeks!",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Talent Acquisition Lead",
      company: "StartupXYZ",
      image: "👨‍💻",
      quote: "The AI analysis is incredibly accurate. It catches skills and experience that we might miss manually.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Recruiting Manager",
      company: "Enterprise Solutions",
      image: "👩‍🦰",
      quote: "Best investment for our HR team. The ROI was visible within the first week of using Resume Rankings.",
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "Free",
      period: "forever",
      credits: "5",
      maxResumes: "5",
      features: [
        "5 resume screenings (one-time)",
        "Max 5 resumes per screening",
        "AI-powered ranking",
        "PDF & DOCX support",
        "Top 5 candidates display",
        "Community support"
      ],
      cta: "Start Free",
      popular: false
    },
    {
      name: "Professional",
      price: "$29",
      period: "/month",
      credits: "50",
      maxResumes: "20",
      features: [
        "50 resume screenings/month",
        "Max 20 resumes per screening",
        "AI-powered ranking",
        "PDF & DOCX support",
        "Detailed candidate analysis",
        "Priority processing",
        "Email support"
      ],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Business",
      price: "$79",
      period: "/month",
      credits: "200",
      maxResumes: "50",
      features: [
        "200 resume screenings/month",
        "Max 50 resumes per screening",
        "AI-powered ranking",
        "Detailed candidate analysis",
        "Priority processing",
        "CSV/PDF export",
        "Priority email support"
      ],
      cta: "Get Started",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      credits: "Unlimited",
      maxResumes: "100",
      features: [
        "Unlimited resume screenings",
        "Max 100 resumes per screening",
        "All Business features",
        "API access (coming soon)",
        "Custom AI training",
        "Dedicated account manager",
        "24/7 phone & email support",
        "SLA guarantee"
      ],
      cta: "Contact Sales",
      popular: false,
      isEnterprise: true
    }
  ];

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev % 3) + 1);
    }, 2000);

    return () => {
      clearInterval(stepInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Resume Rankings
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-600 hover:text-gray-900 transition">Features</a>
            <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition">How it Works</a>
            <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition">Pricing</a>
            <a href="#testimonials" className="text-gray-600 hover:text-gray-900 transition">Reviews</a>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 text-gray-700 hover:text-gray-900 font-medium transition"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-200 transform hover:-translate-y-0.5 transition-all"
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Concept 2 Style (Dashboard Focus) */}
      <section className="max-w-7xl mx-auto px-4 pt-16 pb-20 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-20 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 right-0 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>

        <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-purple-700 rounded-full mb-6 shadow-sm">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Powered by Google Gemini AI</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Smart Resume Rankings
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent pb-2">
                Powered by AI
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Rank candidates instantly with AI precision. Screen <span className="font-bold text-purple-600">100 resumes in 60 seconds</span>, get detailed rankings and analysis, and hire the best talent faster than ever.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={() => navigate('/register')}
                className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-lg hover:border-purple-300 hover:bg-purple-50 transition-all flex items-center justify-center gap-2">
                <Play className="w-5 h-5 text-purple-600" />
                Watch Demo
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">5 Free Screenings</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full">
                <Shield className="w-4 h-4" />
                <span className="font-medium">No Credit Card</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full">
                <Zap className="w-4 h-4" />
                <span className="font-medium">2 Min Setup</span>
              </div>
            </div>
          </div>

          {/* Hero Illustration */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative z-10">
              <HeroIllustration />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-200 rounded-full blur-3xl opacity-50" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-200 rounded-full blur-3xl opacity-50" />
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="bg-white/50 backdrop-blur-sm py-8 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-gray-500 text-sm mb-6">TRUSTED BY RECRUITERS AT</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60">
            {['Google', 'Microsoft', 'Amazon', 'Meta', 'Apple', 'Netflix'].map((company) => (
              <span key={company} className="text-xl font-bold text-gray-400">{company}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: '10,000+', label: 'Resumes Screened', icon: <FileText className="w-6 h-6" />, color: 'blue' },
              { number: '500+', label: 'Happy Recruiters', icon: <Users className="w-6 h-6" />, color: 'purple' },
              { number: '95%', label: 'Accuracy Rate', icon: <Target className="w-6 h-6" />, color: 'green' },
              { number: '20x', label: 'Faster Hiring', icon: <Rocket className="w-6 h-6" />, color: 'orange' }
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-purple-100 transition-all group">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${
                  stat.color === 'blue' ? 'from-blue-100 to-blue-200 text-blue-600' :
                  stat.color === 'purple' ? 'from-purple-100 to-purple-200 text-purple-600' :
                  stat.color === 'green' ? 'from-green-100 to-green-200 text-green-600' :
                  'from-orange-100 to-orange-200 text-orange-600'
                } group-hover:scale-110 transition-transform`}>
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Concept 1 Style (Journey/Process) */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              Simple Process
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to transform your hiring process
            </p>
          </div>

          {/* Process Steps with Illustrations */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 transform -translate-y-1/2 z-0" />

            <div className="grid md:grid-cols-3 gap-8 relative z-10">
              {steps.map((step, i) => (
                <div
                  key={step.num}
                  className={`bg-white rounded-3xl p-8 shadow-lg border-2 transition-all duration-500 ${
                    activeStep === step.num
                      ? 'border-purple-400 shadow-purple-100 scale-105'
                      : 'border-gray-100 hover:border-purple-200'
                  }`}
                >
                  <div className="w-24 h-24 mx-auto mb-6">
                    <ProcessIllustration step={step.num} />
                  </div>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg ${
                    activeStep === step.num
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                      : 'bg-gray-300'
                  }`}>
                    {step.num}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">{step.title}</h3>
                  <p className="text-gray-600 text-center">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Comparison - Concept 3 Style (Transformation) */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              The Transformation
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              From Chaos to <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Clarity</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Before - Grayscale */}
            <div className="relative">
              <div className="absolute -top-4 left-4 px-4 py-2 bg-gray-800 text-white rounded-full text-sm font-bold z-10">
                WITHOUT Resume Rankings
              </div>
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 border-2 border-gray-300 grayscale-[50%]">
                <div className="text-6xl mb-6 text-center opacity-60">😫</div>
                <div className="space-y-4">
                  {[
                    { icon: '⏰', text: '20+ hours per position', stat: 'Wasted' },
                    { icon: '📚', text: '100+ resumes to read manually', stat: 'Overwhelming' },
                    { icon: '🎲', text: 'Inconsistent evaluation', stat: 'Risky' },
                    { icon: '😴', text: 'Missing great candidates', stat: 'Costly' },
                    { icon: '💸', text: 'High cost per hire', stat: '$4,000+' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-white/80 rounded-xl border border-gray-200">
                      <span className="text-2xl">{item.icon}</span>
                      <span className="flex-1 text-gray-600">{item.text}</span>
                      <span className="text-red-500 font-bold text-sm">{item.stat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* After - Vibrant */}
            <div className="relative">
              <div className="absolute -top-4 left-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-bold z-10 shadow-lg">
                WITH Resume Rankings ✨
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 border-2 border-purple-200 shadow-xl shadow-purple-100">
                <div className="text-6xl mb-6 text-center">🚀</div>
                <div className="space-y-4">
                  {[
                    { icon: '⚡', text: 'Screen 100 resumes in 60 seconds', stat: '20x Faster' },
                    { icon: '🤖', text: 'AI analyzes every detail', stat: 'Accurate' },
                    { icon: '📊', text: 'Consistent, objective rankings', stat: 'Fair' },
                    { icon: '🏆', text: 'Find best candidates instantly', stat: 'Efficient' },
                    { icon: '💰', text: 'Reduce hiring costs by 80%', stat: 'Save $3,200' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                      <span className="text-2xl">{item.icon}</span>
                      <span className="flex-1 text-gray-700 font-medium">{item.text}</span>
                      <span className="text-green-600 font-bold text-sm bg-green-50 px-2 py-1 rounded">{item.stat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Powerful <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-gray-600">Everything you need to hire smarter</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Brain />, title: "AI Analysis", desc: "Deep understanding of skills and experience", color: "purple" },
              { icon: <Filter />, title: "Smart Ranking", desc: "Automatic sorting by match score", color: "blue" },
              { icon: <Eye />, title: "Detailed Insights", desc: "Strengths, weaknesses, and fit analysis", color: "green" },
              { icon: <Download />, title: "Easy Export", desc: "Download results as CSV or PDF", color: "orange" },
              { icon: <Shield />, title: "Secure & Private", desc: "Enterprise-grade security", color: "red" },
              { icon: <Zap />, title: "Lightning Fast", desc: "Results in under 60 seconds", color: "yellow" },
              { icon: <Target />, title: "High Accuracy", desc: "95% match with human evaluation", color: "pink" },
              { icon: <ThumbsUp />, title: "Easy to Use", desc: "No training required", color: "indigo" }
            ].map((feature, i) => (
              <div
                key={i}
                className="group p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-lg hover:border-purple-100 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                  feature.color === 'purple' ? 'bg-purple-100 text-purple-600 group-hover:bg-purple-600 group-hover:text-white' :
                  feature.color === 'blue' ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' :
                  feature.color === 'green' ? 'bg-green-100 text-green-600 group-hover:bg-green-600 group-hover:text-white' :
                  feature.color === 'orange' ? 'bg-orange-100 text-orange-600 group-hover:bg-orange-600 group-hover:text-white' :
                  feature.color === 'red' ? 'bg-red-100 text-red-600 group-hover:bg-red-600 group-hover:text-white' :
                  feature.color === 'yellow' ? 'bg-yellow-100 text-yellow-600 group-hover:bg-yellow-600 group-hover:text-white' :
                  feature.color === 'pink' ? 'bg-pink-100 text-pink-600 group-hover:bg-pink-600 group-hover:text-white' :
                  'bg-indigo-100 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white'
                }`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-4">
              ⭐ Customer Stories
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Loved by <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Recruiters</span>
            </h2>
            <p className="text-xl text-gray-600">See what our customers are saying</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center text-3xl">
                    {testimonial.image}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                    <div className="text-sm text-purple-600">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

 {/* Pricing Section */}
      <section id="pricing" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Pricing</span>
            </h2>
            <p className="text-xl text-gray-600">Start free, upgrade as you grow</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Free Plan */}
            <div className="bg-gray-50 rounded-2xl p-6 border-2 border-gray-200">
              <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold">$0</span>
                <span className="text-gray-500">/forever</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Max 5 resumes per screening/job</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Basic AI-powered ranking</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> PDF & DOCX support</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Top 5 candidates display</li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Get Started
              </button>
            </div>

            {/* Professional Plan */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-purple-300 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </span>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold">$29</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 50 resume screenings/month</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Max 20 resumes per screening/job</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Advanced AI matching</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> PDF & DOCX support</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Detailed candidate analysis</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Priority processing</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Email support</li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
              >
                Start Free Trial
              </button>
            </div>

            {/* Business Plan */}
            <div className="bg-purple-50 rounded-2xl p-6 border-2 border-purple-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold">$79</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 200 resume screenings/month</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Up to 50 resumes/job</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> PDF & DOCX support</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Priority AI matching</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Team collaboration</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Detailed candidate analysis</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Utmost Priority processing</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> On-demand Additional Features</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Priority email support</li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Start Free Trial
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-amber-50 rounded-2xl p-6 border-2 border-amber-200">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold">Custom</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Unlimited resume screenings</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Max 100 resumes per screening</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Custom AI models</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Dedicated account manager</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> SSO integration</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 24/7 phone & email support</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> All Business features</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />  Additional Features Provided as per request</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> API access (coming soon)</li>
              </ul>
              <button
                onClick={() => window.location.href = 'mailto:enterprise@resumerankings.com?subject=Enterprise Plan Inquiry'}
                className="w-full py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 500+ recruiters who save 20+ hours per week
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-10 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg hover:shadow-2xl transform hover:-translate-y-1 transition"
          >
            Start Free Trial - 5 Credits Free
          </button>
          <p className="mt-4 text-sm opacity-75">No credit card required • 2-minute setup</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Resume Rankings</span>
              </div>
              <p className="text-gray-400 mb-4">
                AI-Powered Resume Rankings for Smarter Hiring
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => window.open('https://twitter.com', '_blank')}
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => window.open('https://linkedin.com', '_blank')}
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </button>
                <button
                  onClick={() => window.open('https://github.com', '_blank')}
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors"
                  aria-label="GitHub"
                >
                  <Github className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition">API</a></li>
                <li><a href="#" className="hover:text-white transition">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition">About</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                <li><a href="#" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition">Cookie Policy</a></li>
                <li><a href="#" className="hover:text-white transition">GDPR</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <span className="text-gray-400">General Inquiries:</span>
                  <a href="mailto:support@resumerankings.com" className="ml-2 hover:text-white transition">support@resumerankings.com</a>
                </li>
                <li>
                  <span className="text-gray-500">Enterprise:</span>
                  <a href="mailto:enterprise@resumerankings.com" className="ml-2 hover:text-white transition">enterprise@resumerankings.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 Resume Rankings. All rights reserved.</p>
            <p className="text-gray-500 text-sm flex items-center gap-1">
              Made with <Heart className="w-4 h-4 text-red-500 fill-red-500" /> and <Coffee className="w-4 h-4 text-yellow-600" />
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
